import React from 'react';
import { Quote, Award, Users, Theater } from 'lucide-react';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import SEO from '../components/SEO';
import { siteContent } from '../data/siteContent';
import { useTranslation } from '../context/TranslationContext';
import musicals from '../data/musicals';

function BioPage() {
  var { t, language } = useTranslation();
  var bio = siteContent.bio;
  var paragraphs = bio.paragraphs;
  var achievements = bio.achievements;
  var collaborations = bio.collaborations;

  var testimonialsByLang = {
    en: [
      { quote: 'Way to play your ass off, Keep up the great and innovative playing', author: 'Allen Vizzutti' },
      { quote: 'Really a wonderful job on this and I like the selection of music. So nice to hear you playing so very well. Congratulations.', author: 'Charlie Davis' },
      { quote: 'I just Heard your record, wowwww, you can play all the difficult pieces, amazing!!!', author: 'Arturo Sandoval' }
    ],
    es: [
      { quote: 'Que manera de tocar increiblemente bien, sigue con esa forma de tocar tan genial e innovadora', author: 'Allen Vizzutti' },
      { quote: 'Realmente un trabajo maravilloso y me gusta la seleccion de musica. Que lindo escucharte tocar tan bien. Felicitaciones.', author: 'Charlie Davis' },
      { quote: 'Acabo de escuchar tu disco, wowwww, puedes tocar todas las piezas dificiles, increible!!!', author: 'Arturo Sandoval' }
    ],
    ja: [
      { quote: '素晴らしい演奏だ。革新的で素晴らしいプレイを続けてくれ', author: 'Allen Vizzutti' },
      { quote: '本当に素晴らしい仕事で、選曲も気に入りました。あなたの見事な演奏を聴けて嬉しいです。おめでとう。', author: 'Charlie Davis' },
      { quote: '君のレコードを聴いたよ、すごい、難しい曲を全部演奏できるなんて、素晴らしい！！！', author: 'Arturo Sandoval' }
    ],
    de: [
      { quote: 'Was fur ein unglaubliches Spiel. Mach weiter mit deinem grossartigen und innovativen Spielen', author: 'Allen Vizzutti' },
      { quote: 'Wirklich eine wunderbare Arbeit und die Musikauswahl gefallt mir. Schon, Sie so gut spielen zu horen. Herzlichen Gluckwunsch.', author: 'Charlie Davis' },
      { quote: 'Ich habe gerade Ihre Aufnahme gehort, wow, Sie konnen alle schwierigen Stucke spielen, erstaunlich!!!', author: 'Arturo Sandoval' }
    ],
    fr: [
      { quote: 'Quelle facon de jouer magistralement, continuez ce jeu formidable et innovant', author: 'Allen Vizzutti' },
      { quote: 'Vraiment un travail merveilleux et j\'aime la selection musicale. Si agreable de vous entendre jouer si bien. Felicitations.', author: 'Charlie Davis' },
      { quote: 'Je viens d\'ecouter votre disque, wowwww, vous pouvez jouer toutes les pieces difficiles, incroyable !!!', author: 'Arturo Sandoval' }
    ],
    it: [
      { quote: 'Che modo di suonare alla grande, continua con quel modo di suonare fantastico e innovativo', author: 'Allen Vizzutti' },
      { quote: 'Davvero un lavoro meraviglioso e mi piace la selezione musicale. Bello sentirti suonare cosi bene. Congratulazioni.', author: 'Charlie Davis' },
      { quote: 'Ho appena ascoltato il tuo disco, wowwww, riesci a suonare tutti i pezzi difficili, incredibile!!!', author: 'Arturo Sandoval' }
    ]
  };

  var testimonials = testimonialsByLang[language] || testimonialsByLang.en;

  return (
    <Layout>
      <SEO 
        title={t('biography') + ' - Isaías Jiménez'}
        description="Isaías Jiménez - LA-born trumpet virtuoso with 30+ years experience. Jazz, classical, pop & musical theater. Performed with Wynton Marsalis, Arturo Sandoval, Juan Gabriel."
        url="/bio"
        image={bio.heroImage}
      />
      {/* Hero */}
      <section className="relative pt-20">
        <div className="h-[60vh] relative">
          <img
            src={bio.heroImage}
            alt="Isaías Jiménez"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
        </div>
        
        <div className="absolute bottom-0 left-0 right-0 pb-16">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <p className="text-sm tracking-widest uppercase text-[#d4af37] mb-4">{t('biography')}</p>
            <h1 className="text-5xl md:text-7xl font-bold text-white">
              Isaías Jiménez
            </h1>
            <p className="text-xl text-neutral-300 mt-4 max-w-2xl">
              {t('shortBio')}
            </p>
          </div>
        </div>
      </section>

      {/* Bio Content */}
      <section data-testid="bio-content" className="py-24 bg-black">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          <div className="space-y-6">
            <p className="text-lg text-neutral-300 leading-relaxed">{t('bioPara1')}</p>
            <p className="text-lg text-neutral-300 leading-relaxed">{t('bioPara2')}</p>
            <p className="text-lg text-neutral-300 leading-relaxed">{t('bioPara3')}</p>
            <p className="text-lg text-neutral-300 leading-relaxed">{t('bioPara4')}</p>
            <p className="text-lg text-neutral-300 leading-relaxed">{t('bioPara5')}</p>
            <p className="text-lg text-neutral-300 leading-relaxed">{t('bioPara6')}</p>
          </div>
        </div>
      </section>

      {/* Achievements */}
      <section className="py-24 bg-neutral-950">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <div>
              <div className="flex items-center gap-3 mb-8">
                <div className="w-12 h-12 rounded-xl bg-[#d4af37]/10 flex items-center justify-center">
                  <Award className="w-6 h-6 text-[#d4af37]" />
                </div>
                <h3 className="text-2xl font-bold text-white">{t('orchestrasEnsembles')}</h3>
              </div>
              <ul className="space-y-3">
                <li className="flex items-center gap-3 text-neutral-300">
                  <span className="w-2 h-2 rounded-full bg-[#d4af37]" />
                  {achievements[0]}
                </li>
                <li className="flex items-center gap-3 text-neutral-300">
                  <span className="w-2 h-2 rounded-full bg-[#d4af37]" />
                  {achievements[1]}
                </li>
                <li className="flex items-center gap-3 text-neutral-300">
                  <span className="w-2 h-2 rounded-full bg-[#d4af37]" />
                  {achievements[2]}
                </li>
                <li className="flex items-center gap-3 text-neutral-300">
                  <span className="w-2 h-2 rounded-full bg-[#d4af37]" />
                  {achievements[3]}
                </li>
                <li className="flex items-center gap-3 text-neutral-300">
                  <span className="w-2 h-2 rounded-full bg-[#d4af37]" />
                  {achievements[4]}
                </li>
                <li className="flex items-center gap-3 text-neutral-300">
                  <span className="w-2 h-2 rounded-full bg-[#d4af37]" />
                  {achievements[5]}
                </li>
              </ul>
            </div>

            <div>
              <div className="flex items-center gap-3 mb-8">
                <div className="w-12 h-12 rounded-xl bg-[#d4af37]/10 flex items-center justify-center">
                  <Users className="w-6 h-6 text-[#d4af37]" />
                </div>
                <h3 className="text-2xl font-bold text-white">{t('notableCollaborations')}</h3>
              </div>
              <ul className="space-y-3">
                <li className="flex items-center gap-3 text-neutral-300">
                  <span className="w-2 h-2 rounded-full bg-[#d4af37]" />
                  {collaborations[0]}
                </li>
                <li className="flex items-center gap-3 text-neutral-300">
                  <span className="w-2 h-2 rounded-full bg-[#d4af37]" />
                  {collaborations[1]}
                </li>
                <li className="flex items-center gap-3 text-neutral-300">
                  <span className="w-2 h-2 rounded-full bg-[#d4af37]" />
                  {collaborations[2]}
                </li>
                <li className="flex items-center gap-3 text-neutral-300">
                  <span className="w-2 h-2 rounded-full bg-[#d4af37]" />
                  {collaborations[3]}
                </li>
                <li className="flex items-center gap-3 text-neutral-300">
                  <span className="w-2 h-2 rounded-full bg-[#d4af37]" />
                  {collaborations[4]}
                </li>
                <li className="flex items-center gap-3 text-neutral-300">
                  <span className="w-2 h-2 rounded-full bg-[#d4af37]" />
                  {collaborations[5]}
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Musical Theater — performance credits */}
      <section className="py-24 bg-neutral-950">
        <div className="max-w-5xl mx-auto px-6 lg:px-12">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-12 h-12 rounded-xl bg-[#d4af37]/10 flex items-center justify-center">
              <Theater className="w-6 h-6 text-[#d4af37]" />
            </div>
            <div>
              <p className="text-xs tracking-widest uppercase text-[#d4af37]">Musical Theater</p>
              <h2 className="text-3xl md:text-4xl font-bold text-white">Pit Orchestra Credits</h2>
            </div>
          </div>
          <p className="text-neutral-500 text-sm mb-10 ml-15">2002 — Present · {musicals.length} productions</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10">
            {musicals.map(function(m, idx) {
              return (
                <div
                  key={idx}
                  className="flex items-baseline justify-between gap-4 py-3 border-b border-white/5 hover:border-[#d4af37]/30 transition-colors group"
                  data-testid={'musical-row-' + idx}
                >
                  <div className="flex items-baseline gap-3 min-w-0">
                    <span className="text-[#d4af37]/40 text-xs font-mono tabular-nums w-6 text-right">{String(idx + 1).padStart(2, '0')}</span>
                    <div className="min-w-0">
                      <p className="text-white text-base font-medium truncate group-hover:text-[#d4af37] transition-colors">{m.title}</p>
                      <p className="text-neutral-500 text-xs">{m.role}</p>
                    </div>
                  </div>
                  <span className="text-neutral-400 text-xs tabular-nums flex-shrink-0">{m.year}</span>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 bg-black">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <p className="text-sm tracking-widest uppercase text-[#d4af37] mb-4">{t('praise')}</p>
            <h2 className="text-4xl font-bold text-white">{t('whatMastersSay')}</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="p-8 rounded-2xl bg-neutral-900/50 border border-white/5 relative">
              <Quote className="w-10 h-10 text-[#d4af37]/20 absolute top-6 right-6" />
              <p className="text-neutral-300 leading-relaxed mb-6 italic">
                "{testimonials[0].quote}"
              </p>
              <p className="text-[#d4af37] font-semibold">- {testimonials[0].author}</p>
            </div>
            <div className="p-8 rounded-2xl bg-neutral-900/50 border border-white/5 relative">
              <Quote className="w-10 h-10 text-[#d4af37]/20 absolute top-6 right-6" />
              <p className="text-neutral-300 leading-relaxed mb-6 italic">
                "{testimonials[1].quote}"
              </p>
              <p className="text-[#d4af37] font-semibold">- {testimonials[1].author}</p>
            </div>
            <div className="p-8 rounded-2xl bg-neutral-900/50 border border-white/5 relative">
              <Quote className="w-10 h-10 text-[#d4af37]/20 absolute top-6 right-6" />
              <p className="text-neutral-300 leading-relaxed mb-6 italic">
                "{testimonials[2].quote}"
              </p>
              <p className="text-[#d4af37] font-semibold">- {testimonials[2].author}</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-neutral-950">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">{t('exploreInventions')}</h2>
          <p className="text-neutral-400 mb-10">
            {t('beyondPerforming')}
          </p>
          <Link
            to="/shop"
            className="inline-flex items-center gap-2 bg-[#d4af37] text-black hover:bg-white px-8 py-4 rounded-full font-semibold transition-all"
          >
            {t('visitShop')}
          </Link>
        </div>
      </section>
    </Layout>
  );
}

export default BioPage;
